
import Vue from 'vue';
import { UserModel, CourseModel } from '@descomplica/dex-models';
import {
  StudentDocumentsCallout,
  StudentDocumentsCertificateCallout,
} from '@/components/postgradStudentDocuments';

import {
  CollapsibleMenu,
  Navbar,
  DropdownMenuMobile,
  DropDownMenuOption,
  NavigationTabMobile,
  NotificationOptions,
  NotificationBigModal,
  Loading,
  Button,
  Popup,
  PopupActions,
  PopupTitle,
  PopupDescription,
  PopupImage,
  BigToast,
  CustomLink,
  IconButton,
  Icon,
} from '@descomplica/vue-components';

import HelpCenterTooltip from '@/components/HelpCenterTooltip.vue';
import { showEqualWebDrawer } from '../helpers/equalWeb';
import { ENDPOINTS } from '@/settings/endpoints';
import CategoryHelper, { DEFAULT_CATEGORY, getCourseCategory } from '../helpers/category';
import { CategoryService, MenuOption } from '../services/CategoryService';
import Enrollments from '@/services/Enrollments';
import { ChimeraCourseModel } from '@/models/chimera/ChimeraCourseModel';
import { MicrocertificateModel } from '@/models/microcertificates/microcertificate.model';
import { InAppNotification, Recipient } from '@/models/crow/in-app-notification.model';
import Analytics from '@/helpers/analytics';
import redirectIfIsUndergrad from '@/helpers/redirectIfIsUndergrad';
import { EVENT_LABELS } from '@/helpers/analytics/labels';
import { capitalizeAllWords } from '@/helpers/string';
import { handleLapRedirection } from '@/helpers/lap_to_classrooms_migration/redirectToLap';
import FirstWeekBadge from '@/components/popup/postgradBadges/FirstWeek.vue';
import HelloBarTrial from '@/components/HelloBarTrial.vue';
import HelpCenter from '@/components/modals/HelpCenter.vue';
import FinalAssessmentsMenuOption from '@/components/FinalAssessmentsMenuOption.vue';
import { filterBU } from '@/helpers/trial';
import ShowCaseAccordion from '@/components/ShowCaseAccordion.vue';
import { TRIAL_SHOW_CASE_PAGES } from '@/enum/trial/trialShowCasePages.enum';
import MicrocertificatePresentationModal from './modals/microcertificate/postgrad/MicrocertificatePresentationModal.vue';
import { upsertGoogleOptimizeScript } from '@/helpers/googleOptimize';
import {
  shouldShowPostgradMicrocertificatePresentationModalToNewUsers,
  storePostgradMicrocertificatePresentationModalViewedDate,
} from '@/helpers/postgradMicrocertificate/presentationModal';
import { PRESENTATION_MODAL_EXHIBITION_HASH } from '@/helpers/postgradMicrocertificate/constants';
import { DocumentsStatus } from '@/enum/postgradStudentDocuments.enum';
import { CourseCertificateModel } from '@/models/studentDocuments';
import StudentDocumentRequestService from '@/helpers/postgradStudentDocuments/request';
import { StudentDocumentsStatusInfo } from '@/helpers/postgradStudentDocuments/interfaces';
import { PostgradTrial } from '@/models/postgradTrial/PostgradTrialModel';

interface CallToAction {
  text: string;
  action: Function;
}

interface Notification {
  title: string;
  description: string;
  imageSrc: string;
}

interface Data {
  pearsonURL: string | undefined;
  loadLapRedirect: boolean;
  switchingCurrentCourse: boolean;
  mobileModal: boolean;
  notificationsOpened: boolean;
  showNotificationBigModal: boolean;
  showNotificationAlert: boolean;
  callToAction: CallToAction;
  notification: Notification;
  timeouts: Array<number>;
  inTrial: boolean;
  showHelpCenter: boolean;
  isHelpCenterTooltipVisible: boolean;
  helpCenterTooltipTimer: number;
  showMicrocertificatePresentationModal: boolean;
}

interface CoursesForMenu extends CourseModel {
  onClickHandler(): void;
}

interface ChimeraCategory {
  name: string;
  slug: string;
}

interface ChimeraCourse {
  category: ChimeraCategory;
  dexCourseId: number;
  name: string;
  slug: string | null;
  descomplicaCategorySlug: string | null;
  shouldRedirectToClassrooms: boolean;
  onClickHandler(): void;
}

interface CoursesGroupedByCategory {
  category: string | undefined;
  courses: Array<CoursesForMenu | ChimeraCourse>;
}

export default Vue.extend({
  components: {
    ShowCaseAccordion,
    StudentDocumentsCallout,
    StudentDocumentsCertificateCallout,
    Button,
    Navbar,
    CollapsibleMenu,
    NavigationTabMobile,
    DropdownMenuMobile,
    DropDownMenuOption,
    NotificationOptions,
    NotificationBigModal,
    Loading,
    Popup,
    PopupActions,
    PopupImage,
    PopupTitle,
    PopupDescription,
    BigToast,
    FirstWeekBadge,
    HelloBarTrial,
    HelpCenter,
    CustomLink,
    IconButton,
    Icon,
    FinalAssessmentsMenuOption,
    HelpCenterTooltip,
    MicrocertificatePresentationModal,
  },
  data(): Data {
    return {
      pearsonURL: ENDPOINTS.pearson,
      loadLapRedirect: false,
      switchingCurrentCourse: false,
      mobileModal: false,
      showNotificationBigModal: false,
      showNotificationAlert: false,
      notificationsOpened: false,
      notification: {
        title: '',
        description: '',
        imageSrc: '',
      },
      callToAction: {
        text: '',
        action: () => {},
      },
      timeouts: [],
      inTrial: false,
      showHelpCenter: false,
      isHelpCenterTooltipVisible: false,
      helpCenterTooltipTimer: 60000,
      showMicrocertificatePresentationModal: false,
    };
  },
  computed: {
    shouldShowAccessibilityIcon(): boolean {
      return this.$store.getters.shouldShowEqualWebWidget;
    },
    user(): UserModel {
      return this.$store.getters.currentUser;
    },
    userAvatar(): String {
      return this.$store.getters.currentUserAvatar;
    },
    userName(): String {
      return this.$store.getters.currentUserName;
    },
    currentCourse(): CourseModel {
      return this.$store.getters.currentCourse;
    },
    canViewPostgradStudyPlanning(): boolean {
      return this.$store.getters.canViewPostgradStudyPlanning;
    },
    shouldDisplaySocialMetaverse(): boolean {
      return this.$store.getters.shouldDisplaySocialMetaverse;
    },
    isPreparingDownloadMicroCertificate(): boolean {
      return this.$store.getters.isGeneratingMicroCertificate;
    },
    chimeraUserCourses(): ChimeraCourse[] {
      return this.$store.getters.getChimeraUserCourses;
    },
    currentChimeraCourse(): ChimeraCourseModel | null {
      return this.$store.getters.getCurrentChimeraCourse;
    },
    userCoursesGroupedByCategory(): CoursesGroupedByCategory[] {
      const courses: CourseModel[] = this.user.getAllCourses() as CourseModel[];
      if (!courses) return [];

      const uniqueCourses: CourseModel[] = Array.from(
        new Set(courses.map((course: CourseModel) => course.id)),
      ).map((id) => {
        return courses.find((course: CourseModel) => course.id === id);
      }) as CourseModel[];

      const groupedDexCourses = this.groupDexCoursesByCategory(uniqueCourses);
      const allCoursesGroupedByCategory = this.addChimeraCourses(groupedDexCourses);

      return allCoursesGroupedByCategory;
    },
    logoURL(): Object {
      return {
        routerPath: '/',
        routerName: 'home',
      };
    },
    menuOptions(): MenuOption[] {
      return CategoryService.menuOptions(
        this.$store.getters,
        this.$refs as any,
        this.isCurrentRoute,
        this.canViewPostgradStudyPlanning,
        this.shouldDisplaySocialMetaverse,
      );
    },
    backofficeAdminUrl(): string | undefined {
      return this.canViewPostgradStudentDocuments
        ? StudentDocumentRequestService.getSSOEndpoint({
            descomplicaCourseId: this.currentCourse.descomplicaCourseId,
          })
        : ENDPOINTS.jacad;
    },
    customerServiceUrl(): string {
      if (this.currentCourse && this.currentCourse.descomplicaCategorySlug === 'pos') {
        return 'https://no.descomplica.com.br/knowledge/p%C3%B3s-gradua%C3%A7%C3%A3o';
      }
      return 'https://no.descomplica.com.br/knowledge/gradua%C3%A7%C3%A3o';
    },
    profileLink(): string | undefined {
      return ENDPOINTS.alumni;
    },
    microcertificates(): MicrocertificateModel[] {
      return this.$store.getters.userMicrocertificates;
    },
    notifications(): Array<Recipient> {
      return this.$store.getters.userFeaturedNotifications;
    },
    notificationAlert(): Recipient {
      return this.$store.getters.notificationToShowToUser;
    },
    blockList(): any[] {
      return this.$store.getters.blockedUrls;
    },
    canShowNotificationAlert(): boolean {
      const actualRoute = this.$route.path;
      const actualDate = new Date().getTime();
      const blockListValues = Object.values(this.blockList);
      const blockedUrl = blockListValues.find((blocked) => {
        if (actualRoute === '/') return blocked.url === actualRoute;
        return actualRoute.includes(blocked.url);
      });
      const isOnExpirationDate =
        blockedUrl &&
        (!blockedUrl.expiration || new Date(blockedUrl.expiration).getTime() > actualDate);

      return this.notificationAlert && !isOnExpirationDate;
    },
    isNotificationSituational(): boolean {
      return this.notificationAlert.inAppNotification?.priority === 'situational';
    },
    isNotificationImportant(): boolean {
      return this.notificationAlert.inAppNotification?.priority === 'important';
    },
    isPostgraduateCourse(): boolean {
      return this.$store.getters.isPostgraduateCourse;
    },
    renderTrialShowCase(): boolean {
      const showCasePages: string[] = Object.values(TRIAL_SHOW_CASE_PAGES);
      return showCasePages.includes(this.$route?.name as string);
    },
    firstContentConsumedDate(): Date | null {
      return this.$store.getters.firstContentConsumedDate;
    },
    showPostgradMicrocertificatePresentationModalToNewUsers(): boolean {
      const contractStartDate: Date | null = this.$store.getters.contractByCourseId(
        this.currentCourse.id,
      )?.createdAt;

      if (!contractStartDate || !this.firstContentConsumedDate) return false;

      return shouldShowPostgradMicrocertificatePresentationModalToNewUsers({
        firstContentConsumedDate: this.firstContentConsumedDate,
        contractStartDate,
      });
    },
    routeHasMicrocertificatePresentationModalHash(): boolean {
      return this.$route.hash === PRESENTATION_MODAL_EXHIBITION_HASH;
    },
    documentStatus(): StudentDocumentsStatusInfo | null {
      return this.$store.getters.studentDocumentsStatusByCourseId(this.currentCourse.id);
    },
    canViewPostgradStudentDocuments(): boolean {
      return this.$store.getters.canViewPostgradStudentDocuments;
    },
    mostRecentChimeraContractStartDate(): Date {
      return this.$store.getters.mostRecentChimeraContract.startDate;
    },
    hasUserUploadedAllDocuments(): boolean {
      if (!this.documentStatus) {
        return true;
      }

      return this.documentStatus.status === DocumentsStatus.OK;
    },
    canViewPostgradCertificateCallout(): boolean {
      return this.$store.getters.canViewPostgradCertificate;
    },
    canDownloadCertificateForCurrentCourse(): boolean {
      return (
        !!this.$store.getters.certificateByCourseId(this.currentCourse.id) &&
        this.canViewPostgradCertificateCallout
      );
    },
    currentCourseCertificate(): CourseCertificateModel | null {
      return this.$store.getters.certificateByCourseId(this.currentCourse.id);
    },
    shouldGetTrial(): boolean {
      return this.$store.getters.shouldGetTrial;
    },
    trialByDescomplicaCourseId(): PostgradTrial {
      return this.$store.getters.trialByDescomplicaCourseId(this.currentChimeraCourse?._id);
    },
    isTrial(): boolean {
      return !!this.trialByDescomplicaCourseId;
    },
    isCourseEligibleForEarlyGraduation(): boolean {
      return this.$store.getters.earlyGraduationEligibilityByCourseId(this.currentCourse.id);
    },
  },
  watch: {
    async $route(to, from) {
      const GRADUATION = 'graduacao';

      const toBU = filterBU(to.path);
      const fromBU = filterBU(from.path);

      if (toBU !== GRADUATION) {
        this.inTrial = false;
        this.showHelpCenter = false;
      } else if (toBU !== fromBU && (await Enrollments.isInTrial())) {
        this.inTrial = true;
      }

      this.checkMicrocertificatePresentationModalExhibition();
    },
    showNotificationAlert(isOpen: boolean): void {
      if (isOpen || !this.notificationAlert?.inAppNotification) return;

      this.$store.dispatch('SET_RECIPIENT_VIEWED', {
        recipientId: this.notificationAlert.id,
      });

      const isInteractive = !!this.notificationAlert.inAppNotification.callToActionUrl;

      Analytics.trackEvent(EVENT_LABELS.notificationViewed, {
        notification_type: this.notificationAlert.inAppNotification.priority,
        is_interactive: isInteractive,
      });
    },
    notificationAlert: {
      immediate: true,
      handler(): void {
        const { setTimeout } = window;

        this.timeouts.push(
          setTimeout(() => {
            this.showNotificationAlert = true;
          }, 1000),
        );
      },
    },
  },
  async mounted() {
    this.inTrial = await Enrollments.isInTrial();
    this.checkMicrocertificatePresentationModalExhibition();

    Analytics.trackEvent(EVENT_LABELS.postgradSecretaryButtonViewed, {
      has_profile: !!this.documentStatus,
      recent_contract_date: this.mostRecentChimeraContractStartDate.toISOString(),
    });
  },
  beforeDestroy() {
    const { clearTimeout } = window;

    this.timeouts.map((timeout) => clearTimeout(timeout));
  },
  methods: {
    checkMicrocertificatePresentationModalExhibition() {
      if (
        this.routeHasMicrocertificatePresentationModalHash ||
        this.showPostgradMicrocertificatePresentationModalToNewUsers
      ) {
        this.showMicrocertificatePresentationModal = true;
      }
    },
    handleMicrocertificatePresentationModalClose() {
      if (this.routeHasMicrocertificatePresentationModalHash) this.$router.push({ hash: '' });

      this.showMicrocertificatePresentationModal = false;
      storePostgradMicrocertificatePresentationModalViewedDate();
    },
    trackAccessibilityIconClick() {
      Analytics.trackEvent(EVENT_LABELS.acessibilityButtonClick, {
        is_drawer_open_button: true,
        is_inside_class: false,
      });
    },
    async changeCurrentCourse(course: CourseModel) {
      this.changePageTitle(course);
      this.setCurrentCourse(course);
      this.onModalClose();

      this.switchingCurrentCourse = true;
      await this.$store.dispatch('SWITCH_CURRENT_COURSE', { course, user: this.user });

      if (this.shouldGetTrial) {
        await this.$store.dispatch('GET_POSTGRAD_TRIAL', this.currentChimeraCourse);
      }

      if (this.isCourseEligibleForEarlyGraduation === undefined) {
        await this.$store.dispatch(
          'GET_POSTGRAD_EARLY_GRADUATION_ELIGIBILITY_FOR_COURSE',
          this.currentCourse.id,
        );
      }

      if (this.canViewPostgradStudentDocuments && !this.documentStatus && !this.isTrial) {
        this.$store.dispatch('GET_USER_DOCUMENTS_STATUS_BY_COURSE_ID', course);
      }

      if (
        this.canViewPostgradCertificateCallout &&
        !this.currentCourseCertificate &&
        !this.isTrial
      ) {
        this.$store.dispatch('GET_USER_CERTIFICATE_INFO_BY_COURSE_ID', course);
      }

      this.switchingCurrentCourse = false;

      upsertGoogleOptimizeScript(course.descomplicaCategorySlug);

      this.loadLapRedirect = handleLapRedirection({
        currentChimeraCourse: this.currentChimeraCourse,
      });
    },
    showEqualWebDrawerAndTrack(): void {
      this.trackAccessibilityIconClick();

      showEqualWebDrawer();
    },
    changePageTitle(course: CourseModel): void {
      if (course && course.name) {
        document.title = `Descomplica | ${capitalizeAllWords(course.name)}`;
      }
    },
    isCurrentRoute(routeName: string): boolean {
      return this.$route && this.$route.name === routeName;
    },
    setCurrentCourse(currentCourse: CourseModel) {
      this.$store.commit('SET_CURRENT_COURSE', currentCourse);

      if (redirectIfIsUndergrad(currentCourse)) return;

      if (currentCourse.slug) {
        this.$router.push({
          name: 'home',
          params: {
            category: currentCourse.descomplicaCategorySlug || DEFAULT_CATEGORY,
            currentCourse: currentCourse.slug,
          },
        });
      }
    },
    showMobileDropdown() {
      this.mobileModal = true;
    },
    onModalClose() {
      this.mobileModal = false;
    },
    logoutUser() {
      this.$store.commit('SIGNOUT');
      window.location.reload();
    },
    getChimeraOnlyCourses(courses: ChimeraCourse[]) {
      return courses.filter((course) => !course.shouldRedirectToClassrooms);
    },
    groupDexCoursesByCategory(courses: CourseModel[]) {
      const categoriesWithCourses: CoursesGroupedByCategory[] = [];
      courses.forEach((course: CourseModel) => {
        const categoryName = CategoryHelper.getNameBySlug(course.descomplicaCategorySlug);
        const courseWithCategoryInCourseInfo = categoriesWithCourses.find(
          (c: CoursesGroupedByCategory) => c.category === categoryName?.toUpperCase(),
        );

        const courseToPush = Object.assign(course, {
          onClickHandler: () => this.changeCurrentCourse(course),
        });

        if (courseWithCategoryInCourseInfo) {
          courseWithCategoryInCourseInfo.courses.push(courseToPush);
          return;
        }

        categoriesWithCourses.push({
          category: categoryName?.toUpperCase(),
          courses: [courseToPush],
        });
      });

      return categoriesWithCourses;
    },
    addChimeraCourses(coursesGroup: CoursesGroupedByCategory[]): CoursesGroupedByCategory[] {
      const chimeraCourses = this.getChimeraOnlyCourses(this.chimeraUserCourses);

      chimeraCourses.forEach((c) => {
        c.descomplicaCategorySlug = c.category.slug;
        c.onClickHandler = () => this.lapRedirection(c.slug);

        const groupToPush = coursesGroup.find(
          (g) => g.category?.toUpperCase() === c.category.name?.toUpperCase(),
        );

        if (groupToPush) {
          groupToPush.courses.push(c);
          return;
        }

        coursesGroup.push({
          category: c.category.name.toUpperCase(),
          courses: [c],
        });
      });
      return coursesGroup;
    },
    lapRedirection(courseSlug: string | null) {
      this.loadLapRedirect = true;
      window.location.replace(`${ENDPOINTS.lap}/cursos/${courseSlug}`);
    },
    clickDownloadHandler(microcertificate: MicrocertificateModel) {
      this.$store.dispatch('GENERATE_MICROCERTIFICATE', microcertificate);
    },
    onNotificationActionClick() {
      this.loadLapRedirect = true;
      window.location.replace(`${ENDPOINTS.alumni}/notificacoes`);
    },
    notificationActionHandler(recipient: Recipient) {
      const externalUrl = !recipient.inAppNotification.callToActionUrl.startsWith('/');
      if (externalUrl) {
        this.checkExternalUrlAndRedirect(recipient.inAppNotification);
      } else {
        this.$router.push(recipient.inAppNotification.callToActionUrl);
      }
    },
    checkExternalUrlAndRedirect(inAppNotification: InAppNotification): void {
      if (this.isStudentDocumentAnalysisResultNotification(inAppNotification.callToActionUrl)) {
        const idOrgParam = new URL(inAppNotification.callToActionUrl).searchParams.get('idOrg');

        StudentDocumentRequestService.executeSSO({
          redirectToDocumentsPage: true,
          idOrg: idOrgParam,
        });
        this.notificationsOpened = false;
      } else {
        window.open(inAppNotification.callToActionUrl, '_blank');
      }
    },
    onNotificationCardClick(recipient: Recipient) {
      const { inAppNotification } = recipient;

      const hasToShowBigModal = inAppNotification?.longDescription.length > 182;
      const hasCallToActionUrl = inAppNotification.callToActionUrl;

      if (!recipient.viewedAt) {
        this.$store.dispatch('SET_RECIPIENT_VIEWED', { recipientId: recipient.id });
        const isInteractive = !!inAppNotification.callToActionUrl;
        Analytics.trackEvent(EVENT_LABELS.notificationViewed, {
          notification_type: inAppNotification.priority,
          is_interactive: isInteractive,
        });
      }

      this.callToAction = {
        text: inAppNotification.callToActionText,
        action: () => {
          const isExternalUrl =
            !inAppNotification.callToActionUrl.startsWith('/') &&
            !inAppNotification.callToActionUrl.startsWith('#');
          if (isExternalUrl) {
            this.checkExternalUrlAndRedirect(inAppNotification);
          } else {
            this.$router.push(inAppNotification.callToActionUrl);
          }
          Analytics.trackEvent(EVENT_LABELS.notificationInteracted, {
            notification_type: recipient.inAppNotification.priority,
            to_url: recipient.inAppNotification.callToActionUrl,
          });
        },
      };

      if (hasToShowBigModal) {
        this.notification = {
          title: inAppNotification.title,
          description: inAppNotification.longDescription,
          imageSrc: inAppNotification.imageUrl,
        };

        this.notificationsOpened = false;
        this.showNotificationBigModal = true;
        return;
      }

      if (hasCallToActionUrl) {
        this.callToAction.action();
        return;
      }

      this.loadLapRedirect = true;
      window.location.replace(`${ENDPOINTS.alumni}/notificacoes`);
    },
    onPopupActionClick() {
      const { inAppNotification } = this.notificationAlert;

      Analytics.trackEvent(EVENT_LABELS.notificationInteracted, {
        notification_type: inAppNotification.priority,
        to_url: inAppNotification.callToActionUrl,
      });

      const isExternalUrl =
        !inAppNotification.callToActionUrl.startsWith('/') &&
        !inAppNotification.callToActionUrl.startsWith('#');

      if (!isExternalUrl) {
        this.$router.push(inAppNotification.callToActionUrl);
        this.showNotificationAlert = false;
        return;
      }

      if (this.isStudentDocumentAnalysisResultNotification(inAppNotification.callToActionUrl)) {
        const idOrgParam = new URL(inAppNotification.callToActionUrl).searchParams.get('idOrg');

        StudentDocumentRequestService.executeSSO({
          redirectToDocumentsPage: true,
          idOrg: idOrgParam,
        });
        this.showNotificationAlert = false;
        return;
      }

      window.open(inAppNotification.callToActionUrl);

      this.showNotificationAlert = false;
    },
    onCtaClick() {
      this.$store.dispatch('SET_RECIPIENT_VIEWED', {
        recipientId: this.notificationAlert.id,
      });
      Analytics.trackEvent(EVENT_LABELS.notificationViewed, {
        notification_type: this.notificationAlert.inAppNotification.priority,
        is_interactive: true,
      });

      Analytics.trackEvent(EVENT_LABELS.notificationInteracted, {
        notification_type: this.notificationAlert.inAppNotification.priority,
        to_url: this.notificationAlert.inAppNotification.callToActionUrl,
      });
    },
    goToEnrollments(): void {
      Analytics.trackEvent(EVENT_LABELS.buttonClicked, {
        button: 'trial-to-enroll-menu',
      });

      window.open(process.env.VUE_APP_ENROLLMENTS);
    },
    goToDocumentsPage(): void {
      Analytics.trackEvent(EVENT_LABELS.buttonClicked, {
        in_postgrad_documents: true,
        course: this.currentCourse.name,
        category: getCourseCategory(this.currentCourse),
      });
      this.$router.push({
        name: 'documents',
      });
      this.mobileModal = false;
    },
    onClickCustomerService(): void {
      Analytics.trackEvent(EVENT_LABELS.buttonClicked, {
        button: 'help-center-submenu',
      });
    },
    closeHelpCenterTooltip(): void {
      if (!localStorage.getItem('dontShowHelpCenterTooltip')) {
        localStorage.setItem('dontShowHelpCenterTooltip', 'true');
      }
      this.isHelpCenterTooltipVisible = false;
    },
    helpCenterHandler(): void {
      this.showHelpCenter = !this.showHelpCenter;
      this.closeHelpCenterTooltip();
    },
    isStudentDocumentAnalysisResultNotification(notificationUrl: string): boolean {
      return (
        new URL(notificationUrl).pathname ===
        new URL(StudentDocumentRequestService.getSSOEndpoint()).pathname
      );
    },
  },
});
